import { Table, message } from 'antd';
import CopyOutlined from '@/assets/icon/copy.svg?react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ColumnsType } from 'antd/es/table';
import { GetRowKey } from 'antd/es/table/interface';
// 初始化dayjs插件
dayjs.extend(relativeTime);

// 通用数据接口
export interface BaseDataItem {
  [key: string]: any;
}

// 分页接口
export interface PaginationConfig {
  current: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: string[];
  onChange: (page: number, pageSize: number) => void;
}

// 统计信息接口
export interface StatsInfo {
  label: string;
  value: string | number;
  highlight?: boolean;
}

// 组件Props接口
interface DataTableProps<T extends BaseDataItem> {
  data: T[];
  columns: ColumnsType<T>;
  loading?: boolean;
  pagination?: PaginationConfig;
  onRowClick?: (record: T) => void;
  statsInfo?: StatsInfo;
  className?: string;
  rowKey?: string | GetRowKey<any>;
}

const PAGE_SIZE_OPTIONS = ['10', '20', '50'];

export default function DataTable<T extends BaseDataItem>({ data, rowKey, columns, loading = false, pagination, onRowClick, statsInfo, className }: DataTableProps<T>) {
  return (
    <TableContainer className={className}>
      {/* 统计信息 */}
      {statsInfo && (
        <StatsText>
          {statsInfo.label} <span className="highlight">{typeof statsInfo.value === 'number' ? statsInfo.value.toLocaleString() : statsInfo.value}</span> found
        </StatsText>
      )}

      {/* 表格区域 */}
      <StyledTable
        columns={columns as any}
        dataSource={data}
        loading={loading}
        pagination={
          pagination
            ? {
                ...pagination,
                position: ['bottomCenter'],
                showSizeChanger: true,
                showQuickJumper: false,
                showTotal: undefined,
                pageSizeOptions: pagination?.pageSizeOptions || PAGE_SIZE_OPTIONS,

                itemRender: (current: any, type: any, originalElement: any) => {
                  if (type === 'page') {
                    return <PageNumber className={current === pagination?.current ? 'active' : ''}>{current}</PageNumber>;
                  }
                  if (type === 'prev') {
                    return <PageArrow>‹</PageArrow>;
                  }
                  if (type === 'next') {
                    return <PageArrow>›</PageArrow>;
                  }
                  return originalElement;
                },
              }
            : false
        }
        rowKey={rowKey || 'id'}
        scroll={{ x: 1280 }}
        onRow={(record: any) => ({
          onClick: () => onRowClick?.(record),
          style: { cursor: onRowClick ? 'pointer' : 'default' },
        })}
        size="middle"
      />
    </TableContainer>
  );
}

// 导出复制图标组件供外部使用
export const CopyIcon = ({ text, className = 'copy_icon' }: { text: string; className?: string }) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  return (
    <CopyIconContainer
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        copyToClipboard(text);
      }}
    >
      <CopyOutlined />
    </CopyIconContainer>
  );
};

// Styled Components
const TableContainer = styled.div`
  background: #ffffff;
  overflow: hidden;
`;

const StatsText = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: rgba(16, 41, 0, 0.7);
  margin-bottom: 24px;

  .highlight {
    font-weight: 500;
    color: #141413;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    font-family: 'Poppins', sans-serif;
  }

  .ant-table-container {
    border: none;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  .ant-table-thead > tr > th {
    background: transparent;
    border-bottom: 1px solid rgba(16, 41, 0, 0.1);
    border-right: none !important;
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #141413;
    padding: 10px 24px;
    height: 56px;

    &:first-child {
      padding-left: 0;
      text-align: left;
    }

    &:last-child {
      padding-right: 0;
      text-align: right;
    }

    &:nth-child(2) {
      text-align: left;
    }

    &:nth-child(3) {
      text-align: left;
    }

    &:nth-child(4) {
      text-align: left;
    }

    &:nth-child(5) {
      text-align: center;
    }

    &:nth-child(6) {
      text-align: center;
    }

    &:nth-child(7) {
      text-align: right;
    }

    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid rgba(16, 41, 0, 0.1);
    border-right: none !important;
    padding: 10px 24px;
    height: 56px;
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    color: #141413;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }

    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(166, 235, 122, 0.05);
  }

  .ant-table-pagination {
    margin-top: 40px;
    text-align: center;
  }

  .ant-table-pagination .ant-pagination-item {
    background: #ffffff;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    margin: 0 2px;
  }

  .ant-table-pagination .ant-pagination-item a {
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    color: #141413;
    line-height: 30px;
  }

  .ant-table-pagination .ant-pagination-item-active {
    background: #141413;
  }

  .ant-table-pagination .ant-pagination-prev,
  .ant-table-pagination .ant-pagination-next {
    background: #ffffff;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
  }

  .ant-pagination-options {
    .ant-select {
      .ant-select-selector {
        background: #ffffff;
        border-color: transparent !important;
        box-shadow: none !important;
        border-radius: 4px;
        height: 32px;
        font-family: 'Poppins', sans-serif;
        font-size: 14px;
        color: #141413;
        padding: 4px 12px;
      }

      .ant-select-selection-item {
        font-family: 'Poppins', sans-serif;
        font-size: 14px;
        color: #141413;
        line-height: 22px;
      }

      .ant-select-arrow {
        color: #141413;
        font-size: 12px;
        transform: translateY(4px);
      }
    }
  }
`;

const CopyIconContainer = styled.span`
  opacity: 0.6;
  cursor: pointer;
  font-size: 14px;
  color: #141413;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 1;
  }
`;

const PageNumber = styled.div`
  background: #ffffff;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #141413;
  cursor: pointer;

  &.active {
    background: #141413;
    color: #ffffff;
  }
`;

const PageArrow = styled.div`
  background: #ffffff;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #141413;
  cursor: pointer;
`;
